import { Link, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button } from '@mui/material';

import { useGetProviderInfoQuery } from '../store/api';
import { AppHeader } from './app-header';
import { LoadingState } from './loading-state';
import { WorkspaceSidebar } from './org-page-redesign/WorkspaceSidebar';
import { ProviderPassport } from './provider-passport';
import { buildProviderNavGroups } from './provider-nav';

/**
 * Route layout that wraps provider subpages in the provider workspace shell:
 * the WorkspaceSidebar nav rail plus the sticky ProviderPassport identity
 * band, mirroring the provider detail page. Selecting a rail entry navigates
 * back to the detail page's section via its ?section= deep link, so the rail
 * behaves identically here and there. Passport contact actions also route to
 * the detail page, where the edit dialogs live.
 */
export function ProviderWorkspaceLayout() {
  const { providerId: urlProviderId } = useParams<{ providerId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const providerId = urlProviderId ? decodeURIComponent(urlProviderId) : null;

  const { data: provider, isLoading } = useGetProviderInfoQuery(
    { providerId: providerId ?? '' },
    { skip: !providerId },
  );

  // Which rail entry this subpage belongs to, from the path segment after
  // the provider id (e.g. .../encounters/<encounterId> → "encounters").
  const activeKey = location.pathname.split('/').filter(Boolean)[3] ?? 'overview';

  if (!providerId || (!isLoading && !provider)) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Provider not found</h2>
            <p className="text-gray-600 mt-2">Unable to load provider details.</p>
            <Link to="/admin-v2">
              <Button className="mt-4">Back to Search</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  if (isLoading || !provider) {
    return <LoadingState message="Loading provider details..." />;
  }

  const providerPath = `/admin-v2/provider/${encodeURIComponent(providerId)}`;
  const goToSection = (key: string) => {
    // Resources with full subpages live inside this layout; everything else
    // is a section of the provider detail page.
    if (['encounters', 'appointments', 'transcriptions', 'notes', 'resources'].includes(key)) {
      navigate(`${providerPath}/${key}`);
      return;
    }
    navigate(`${providerPath}?section=${encodeURIComponent(key)}`);
  };

  return (
    <div className="flex h-full overflow-hidden bg-gray-50">
      <WorkspaceSidebar
        groups={buildProviderNavGroups(!!provider.organization_id, undefined, undefined)}
        activeKey={activeKey}
        onSelect={goToSection}
        contextStack={[
          { label: 'Admin v2', onSelect: () => navigate('/admin-v2') },
          ...(provider.organization_id
            ? [
                {
                  label: provider.organization_name || 'Organization',
                  onSelect: () =>
                    navigate(`/admin-v2/organization/${provider.organization_id}`),
                },
              ]
            : []),
          {
            label: provider.name || 'User',
            chip: 'User',
            onSelect: () => goToSection('overview'),
          },
        ]}
      />

      <div className="min-w-0 flex-1 overflow-y-auto">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ProviderPassport
            provider={provider}
            onChangeEmail={() => goToSection('overview')}
            onEditPhone={() => goToSection('overview')}
            onDeletePhone={() => goToSection('overview')}
            onOpenOrganization={organizationId =>
              navigate(`/admin-v2/organization/${organizationId}`)
            }
          />

          <div className="py-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
